import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LifeBuoy, Plus, Send, Loader2, Clock, CheckCircle2, ChevronLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import ContactSupportButton from '../components/ContactSupportButton';

const API_URL = window.__BACKEND_URL__;

const CATEGORY_LABELS = {
  bug: 'Bug',
  billing: 'Billing',
  account: 'Account',
  content: 'Content',
  other: 'Other',
};

const STATUS_STYLE = {
  open:         { label: 'Open',         className: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  acknowledged: { label: 'In progress',  className: 'bg-[#002FA7]/15 text-[#002FA7] border-[#002FA7]/30' },
  resolved:     { label: 'Resolved',     className: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  dismissed:    { label: 'Dismissed',    className: 'bg-theme-hover text-theme-muted border-theme' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.open;
  return (
    <Badge variant="outline" className={`text-xs ${s.className}`} data-testid={`ticket-status-${status}`}>
      {s.label}
    </Badge>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

/**
 * User-facing "My Support Tickets" page.
 *
 * Lists tickets returned by `GET /api/support/issues` (already user-scoped
 * server-side — see routers/support.py:160). Selecting a ticket loads the
 * full record via `GET /api/support/issues/{id}` and renders the reply
 * thread. The user can post a new reply (which automatically reopens
 * resolved/dismissed tickets per backend rule).
 *
 * Reuses `ContactSupportButton` for the "Open new ticket" CTA so the only
 * support-ticket-creation path stays unified.
 */
export default function MySupportTicketsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/support/issues`, { withCredentials: true });
      setTickets(data.issues || []);
    } catch {
      toast.error('Could not load your tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) fetchTickets(); }, [user, fetchTickets]);

  const openTicket = async (t) => {
    setSelected({ ...t, _loadingDetail: true });
    try {
      const { data } = await axios.get(`${API_URL}/api/support/issues/${t.id}`, { withCredentials: true });
      setSelected(data);
    } catch {
      toast.error('Could not open ticket');
      setSelected(null);
    }
  };

  const submitReply = async () => {
    if (reply.trim().length < 5) {
      toast.error('Please type a longer reply (min. 5 characters)');
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/support/issues/${selected.id}/reply`,
        { message: reply.trim() },
        { withCredentials: true },
      );
      toast.success('Reply sent');
      setReply('');
      // Refresh detail + list so status (e.g. reopened) updates everywhere.
      const [detail, list] = await Promise.all([
        axios.get(`${API_URL}/api/support/issues/${selected.id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/support/issues`, { withCredentials: true }),
      ]);
      setSelected(detail.data);
      setTickets(list.data.issues || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-theme">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-tickets-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-[#002FA7]/15 border border-[#002FA7]/30 flex items-center justify-center">
              <LifeBuoy className="h-5 w-5 text-[#002FA7]" />
            </div>
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-theme-text" data-testid="my-tickets-title">
                My support tickets
              </h1>
              <p className="text-theme-muted text-sm">View your support history and admin replies.</p>
            </div>
          </div>
          <ContactSupportButton
            className="bg-[#002FA7] hover:bg-[#0055FF] text-white"
            showLabel={true}
          />
        </div>

        {selected ? (
          <div className="space-y-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSelected(null); setReply(''); }}
              className="text-theme-muted hover:text-theme-text"
              data-testid="ticket-back-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to tickets
            </Button>

            <div className="rounded-xl bg-theme-surface border border-theme p-5">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <Badge variant="outline" className="text-xs bg-theme-hover text-theme-muted border-theme">
                  {CATEGORY_LABELS[selected.category] || selected.category}
                </Badge>
                <StatusBadge status={selected.status} />
                <span className="text-xs text-theme-muted ml-auto inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(selected.created_at)}
                </span>
              </div>
              <h2 className="text-lg font-semibold text-theme-text mb-2" data-testid="ticket-subject">
                {selected.subject}
              </h2>
              <p className="text-theme-text whitespace-pre-wrap text-sm leading-relaxed">{selected.message}</p>
            </div>

            {/* Reply thread */}
            {(selected.replies || []).map((r, i) => {
              const adminReply = r.by_role === 'admin';
              const displayName = adminReply
                ? (r.by_name || 'Admin Support')
                : (r.by_name || 'You');
              const position = adminReply
                ? (r.by_position || 'CCUK Support')
                : null;

              const initials = displayName
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((part) => part[0]?.toUpperCase() || '')
                .join('') || 'AS';

              return (
                <div
                  key={i}
                  className={`rounded-xl border p-4 ${
                    adminReply
                      ? 'bg-[#002FA7]/5 border-[#002FA7]/30'
                      : 'bg-theme-surface border-theme'
                  }`}
                  data-testid={`ticket-reply-${i}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {adminReply && (
                        <Avatar
                          className="h-9 w-9 shrink-0"
                          data-testid={`ticket-admin-avatar-${i}`}
                        >
                          <AvatarImage
                            src={r.by_avatar_url || undefined}
                            alt={displayName}
                          />
                          <AvatarFallback className="bg-[#002FA7] text-white text-xs font-semibold">
                            {initials}
                          </AvatarFallback>
                        </Avatar>
                      )}

                      <div className="min-w-0">
                        <p
                          className="text-xs font-semibold text-theme-text truncate"
                          data-testid={`ticket-reply-name-${i}`}
                        >
                          {displayName}
                        </p>

                        {adminReply && (
                          <p
                            className="text-[11px] text-theme-muted truncate"
                            data-testid={`ticket-reply-position-${i}`}
                          >
                            {position} · CCUK Admin
                          </p>
                        )}
                      </div>
                    </div>

                    <span className="text-xs text-theme-muted shrink-0">
                      {formatDate(r.at)}
                    </span>
                  </div>

                  <p className="text-sm text-theme-text whitespace-pre-wrap leading-relaxed">
                    {r.message}
                  </p>
                </div>
              );
            })}

            {/* Reply composer (allowed even on resolved — backend will reopen) */}
            <div className="rounded-xl bg-theme-surface border border-theme p-4">
              <p className="text-sm font-semibold text-theme-text mb-2">
                {selected.status === 'resolved' || selected.status === 'dismissed'
                  ? 'Need more help? Reply to reopen this ticket.'
                  : 'Add a reply'}
              </p>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={4}
                placeholder="Type your reply..."
                className="bg-theme/50 border-theme text-theme-text mb-3"
                data-testid="ticket-reply-input"
              />
              <Button
                onClick={submitReply}
                disabled={sending || reply.trim().length < 5}
                className="bg-[#002FA7] hover:bg-[#0055FF] text-white"
                data-testid="ticket-reply-submit"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                Send reply
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-theme-muted" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-12 rounded-xl bg-theme-surface border border-theme">
            <CheckCircle2 className="h-10 w-10 text-theme-muted mx-auto mb-3" />
            <p className="text-theme-text font-semibold mb-1">No tickets yet</p>
            <p className="text-theme-muted text-sm mb-4">
              When you contact support, your tickets and admin replies will appear here.
            </p>
            <ContactSupportButton
              className="bg-[#002FA7] hover:bg-[#0055FF] text-white"
              showLabel={true}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3" data-testid="tickets-list">
            {tickets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => openTicket(t)}
                className="text-left rounded-xl bg-theme-surface border border-theme p-4 hover:border-[#002FA7]/50 hover:shadow-elev-1 transition-all"
                data-testid={`ticket-row-${t.id}`}
              >
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <Badge variant="outline" className="text-xs bg-theme-hover text-theme-muted border-theme">
                    {CATEGORY_LABELS[t.category] || t.category}
                  </Badge>
                  <StatusBadge status={t.status} />
                  <span className="text-xs text-theme-muted ml-auto inline-flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatDate(t.created_at)}
                  </span>
                </div>
                <p className="font-semibold text-theme-text text-sm line-clamp-1">{t.subject}</p>
                <p className="text-theme-muted text-xs line-clamp-2 mt-1">{t.message}</p>
                {(t.replies?.length || 0) > 0 && (
                  <p className="text-xs text-[#002FA7] mt-2 font-medium">
                    {t.replies.length} {t.replies.length === 1 ? 'reply' : 'replies'}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
